import React from "react";
import {
  Box,
  Typography,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Paper,
  Chip,
  Collapse
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Lock,
  Group,
  Shield,
  Gavel
} from "@mui/icons-material";

interface PremiumTimelineTrackerProps {
  currentStatus: string;
  claimAmount: number;
  complexityScore: number;
  sessions: any[];
  currentUserId: number;
  isAdmin: boolean;
  isNeutral: boolean;
  neutralAssignment?: any;
}

const STATUS_STEPS = [
  { statusKey: "INITIATED", label: "Notice & Initiation", desc: "Legal notice served under statutory timeline" },
  { statusKey: "CONSENTED", label: "Respondent Onboarding", desc: "Respondent reviews notice & submits consent" },
  { statusKey: "NEUTRAL_ASSIGNED", label: "Neutral Assignment", desc: "Panel expert appointed by administrator" },
  { statusKey: "COLLABORATION", label: "Dialogue & Caucus", desc: "Online joint sessions & private caucuses" },
  { statusKey: "RESOLVED", label: "Closure & eSign", desc: "Settlement draft prepared & digitally eSigned" }
];

export const PremiumTimelineTracker: React.FC<PremiumTimelineTrackerProps> = ({
  currentStatus,
  claimAmount,
  complexityScore,
  sessions,
  currentUserId,
  isAdmin,
  isNeutral,
  neutralAssignment
}) => {
  const getActiveStepIndex = (): number => {
    const status = (currentStatus || "INITIATED").toUpperCase();
    
    // Fallbacks and exact mappings
    let index = 0;
    if (status === "INITIATED") index = 0;
    else if (status === "RESPONDED" || status === "AWAITING_CONSENT") index = 1;
    else if (status === "TRIAGED" || status === "NEUTRAL_ASSIGNED") index = 2;
    else if (["NEGOTIATION", "MEDIATION", "ARBITRATION"].includes(status)) index = 3;
    else if (["RESOLVED", "REJECTED", "DEFAULT_AWARDED", "CLOSED"].includes(status)) index = 4;
    
    if (neutralAssignment && index < 2) {
      index = 2;
    }
    if (sessions && sessions.length > 0 && index < 3) {
      index = 3;
    }
    return index;
  };

  const activeIndex = getActiveStepIndex();

  // Enforce Section 22 Mediation Act Confidentiality: Filter caucus sessions dynamically
  const visibleSessions = sessions.filter(session => {
    const isPrivate = session.sessionType === "PrivateCaucus";
    if (!isPrivate) return true; // Joint sessions are public to all case participants
    
    // Private caucus: visible only to creator (Neutral), admin, or the designated party
    const targetPartyId = session.privateForPartyId || session.PrivateForPartyId;
    if (isAdmin || isNeutral) return true;
    return targetPartyId && Number(targetPartyId) === Number(currentUserId);
  });

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: "24px",
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        boxShadow: "0 4px 20px -2px rgba(148, 163, 184, 0.08)",
        mb: 4
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 4 }}>
        <Shield sx={{ color: "#283165", fontSize: 28 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: "Outfit", color: "#0f172a" }}>
            Dispute Resolution Timeline Tracker
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
            Enforcing compliance under Section 22 Mediation Act 2023
          </Typography>
        </Box>
      </Stack>

      <Stepper activeStep={activeIndex} alternativeLabel>
        {STATUS_STEPS.map((step, idx) => {
          let isCompleted = idx < activeIndex;
          const isActive = idx === activeIndex;
          
          // If Neutral is assigned early, Respondent Onboarding might not be complete yet.
          if (idx === 1 && ["INITIATED", "AWAITING_CONSENT"].includes((currentStatus || "").toUpperCase())) {
            isCompleted = false;
          }
          
          let stepDesc = step.desc;
          if (step.statusKey === "NEUTRAL_ASSIGNED" && neutralAssignment) {
            stepDesc = `Assigned: ${neutralAssignment.firstName} ${neutralAssignment.lastName} (${neutralAssignment.consentStatus || "Pending"})`;
          }
          
          return (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: isCompleted ? "#10b981" : isActive ? "#283165" : "#e2e8f0",
                      color: "white",
                      boxShadow: isActive ? "0 0 0 4px rgba(40, 49, 101, 0.2)" : "none",
                      transition: "all 0.3s ease"
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircle sx={{ fontSize: 18 }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                )}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isActive ? "#283165" : "#1e293b",
                    fontSize: "0.9rem",
                    fontFamily: "Outfit"
                  }}
                >
                  {step.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748b", mt: 0.5, display: "block", px: 1 }}>
                  {stepDesc}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Empanelled Neutral Information Block */}
      {neutralAssignment && (
        <Box 
          sx={{ 
            mt: 4, 
            p: 2.5, 
            borderRadius: "16px", 
            bgcolor: "rgba(40, 49, 101, 0.03)", 
            border: "1px solid rgba(40, 49, 101, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                bgcolor: "rgba(40, 49, 101, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Gavel sx={{ color: "#283165", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 850, color: "#0f172a", fontFamily: "Outfit" }}>
                Empanelled Neutral: {neutralAssignment.firstName} {neutralAssignment.lastName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.2 }}>
                Email: {neutralAssignment.email} | Status Date: {new Date(neutralAssignment.statusDate).toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <Chip 
            label={neutralAssignment.consentStatus === "Confirmed" ? "Assignment Confirmed" : "Awaiting Neutral Consent"} 
            color={neutralAssignment.consentStatus === "Confirmed" ? "success" : "warning"}
            size="small"
            sx={{ fontWeight: 700, borderRadius: "8px", px: 1 }}
          />
        </Box>
      )}

      {/* Show active sessions inside Dialogue room step (index 3) when active */}
      <Collapse in={activeIndex >= 3}>
        <Box sx={{ mt: 4, pt: 3, borderTop: "1px dashed #cbd5e1" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", mb: 2, fontFamily: "Outfit" }}>
            Active Dialogue Session Schedule
          </Typography>
          
          {visibleSessions.length > 0 ? (
            <Stack spacing={2}>
              {visibleSessions.map((session, index) => {
                const isPrivate = session.sessionType === "PrivateCaucus";
                return (
                  <Paper
                    key={session.id || index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: "16px",
                      background: isPrivate ? "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)" : "linear-gradient(135deg, #f0fdf4 0%, #f0fdf4 100%)",
                      borderColor: isPrivate ? "#fecdd3" : "#bbf7d0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          bgcolor: isPrivate ? "rgba(239, 68, 68, 0.1)" : "rgba(34, 197, 94, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {isPrivate ? <Lock sx={{ color: "#ef4444" }} /> : <Group sx={{ color: "#22c55e" }} />}
                      </Box>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isPrivate ? "#991b1b" : "#166534" }}>
                            {isPrivate ? "Private Caucus Room" : "Joint Dispute Conference"}
                          </Typography>
                          {isPrivate && (
                            <Chip
                              label="Confidential"
                              size="small"
                              color="error"
                              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: isPrivate ? "#b91c1c" : "#15803d" }}>
                          Scheduled: {new Date(session.scheduledStartTime).toLocaleString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
              No scheduled joint or private caucus sessions visible under your role credentials.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};
